import React, { useState, useRef } from 'react';
import './Experience.css';
import nvidiaLogo from './nvidia.png';
import appleLogo from './apple.png';
import shopifyLogo from './shopify.png';
import mawarilogo from './mawari.png';
import nboLogo from './nbo.jpg';
import stanfordLogo from './stanford.png';
import startupLogo from './stealth.png';
import swiftLogo from './swift.png';
import mathletesLogo from './mathletes.png';
import webgoLogo from './webgo.png';
import waterlooLogo from './uwaterloo-logo.png';
import uwrtLogo from './uwrt-logo.png';

const Experience = () => {
  const [activeCategory, setActiveCategory] = useState('industry');
  const [activeExperience, setActiveExperience] = useState('nvidia');
  const cardRefs = useRef({});
  const [fadeOut, setFadeOut] = useState(false);

  const experienceData = {
    industry: {
      nvidia: {
        id: 'nvidia',
        title: 'Software Engineer Intern',
        company: 'NVIDIA',
        team: 'AI Inference Systems',
        badge: 'Incoming',
        period: 'Sep 2026 – Dec 2026',
        roleType: 'AI Inference Systems',
        imageUrl: nvidiaLogo,
        skills: ['Python', 'C++', 'GPU', 'MLIR', 'vLLM', 'MLPerf', 'CUDA', 'CUTLASS', 'Nsight'],
        link: 'https://www.nvidia.com',
        summary: 'Focused on high-performance AI inference acceleration and LLM runtime optimization.',
        bulletPoints: [
          'Optimizing <strong>vLLM</strong> performance through deep learning compiler kernel fusion, high-throughput <strong>CUTLASS</strong> kernels, and <strong>CUDA Graphs</strong> to minimize GPU launch overheads and latency in enterprise inference pipelines.'
        ]
      },
      apple: {
        id: 'apple',
        title: 'Machine Learning Intern',
        company: 'Apple',
        team: 'On-Device ML',
        period: 'May 2026 – Aug 2026',
        roleType: 'CoreAI & Compiler Systems',
        imageUrl: appleLogo,
        skills: ['Python', 'C++', 'MLIR', 'LLVM', 'DSLs', 'GPU', 'Apple Neural Engine (ANE)', 'AMX'],
        link: 'https://www.apple.com',
        summary: 'Worked on compiler lowerings and kernel DSL layout optimization for on-device AI accelerators.',
        bulletPoints: [
          'Built an <strong>AST-visitor rewriter</strong> to partially unroll loop lowerings into an <strong>MLIR tile dialect</strong> for a <strong>13% speedup</strong> via reduced register pressure and cache reuse to integrate into on-device AI (<strong>CoreAI</strong>) workloads.',
          'Inverted multi-level GPU hardware tile layouts in a kernel DSL using a <strong>bit-permutation basis table</strong>, reducing layout-conversion analysis complexity from <strong>2<sup>n</sup></strong> position enumeration to <strong>O(n<sup>2</sup>)</strong> basis comparisons.',
          'Amortized matmul loads with <strong>register tiling (unroll-and-jam)</strong> and a linear-scan register allocator in a MLIR backend targeting <strong>AMX intrinsics</strong>, cutting inner-loop loads by <strong>50%</strong> to double arithmetic intensity.'
        ]
      },
      shopify: {
        id: 'shopify',
        title: 'Software Engineer Intern',
        company: 'Shopify',
        team: 'Core Inventory',
        period: 'Sep 2025 – Dec 2025',
        roleType: 'Core Infrastructure & APIs',
        imageUrl: shopifyLogo,
        skills: ['React', 'Ruby on Rails', 'GraphQL', 'MySQL', 'BigQuery', 'Graphite'],
        link: 'https://www.shopify.com',
        summary: 'Engineered merchant platform consistency and low-latency GraphQL transactional infrastructure.',
        bulletPoints: [
          'Built an <strong>idempotency framework</strong> in Ruby on Rails with MySQL, GraphQL directives, and SHA-256 hashing to handle network retries and prevent duplicate mutations for <strong>2M+ merchants</strong>.',
          'Implemented <strong>optimistic concurrency control (CAS atomicity)</strong> and mutation validators, detecting stale reads to ensure consistency, prevent race conditions, and reduce inventory conflicts.',
          'Optimized queries with <strong>batch loaders</strong>, lazy & deferred execution, eliminating N+1 queries and boosting query latency.'
        ]
      },
      mawari: {
        id: 'mawari',
        title: 'Software Developer Intern',
        company: 'Mawari',
        team: 'Network',
        period: 'Jan 2025 – Apr 2025',
        roleType: 'Distributed Systems & DePIN',
        imageUrl: mawarilogo,
        skills: ['Golang', 'LibP2P', 'WebRTC', 'Docker', 'Redis', 'PostgreSQL', 'Jet', 'SQLx'],
        link: 'https://mawari.net',
        summary: 'Designed multi-threaded scheduling daemons and decentralized node controllers for spatial streaming.',
        bulletPoints: [
          'Architected a <strong>multi-threaded orchestrator</strong> and K8s-style scheduler in 8k+ lines of Go for a <strong>DePIN P2P system</strong>, utilizing a closed-loop controller to cut latency for XR/3D spatial streaming over full-duplex TCP via WebRTC.',
          'Implemented a daemon with algorithms for <strong>Bipartite Matching</strong> and <strong>Stable Task Assignment (ESTA)</strong>, improving load-balancing by <strong>15%</strong> to process 150+ tasks with reduced match waste & downtime.',
          'Built a <strong>CORC node reputation system</strong> using LibP2P with Mawari tokens to strengthen security and GPU-node reliability.'
        ]
      },
      nbo: {
        id: 'nbo',
        title: 'UI/UX Designer & Product Intern',
        company: 'National Bank of Oman',
        team: 'Customer Experience',
        period: 'Aug 2024 – Sep 2024',
        roleType: 'Fintech Design & Engineering',
        imageUrl: nboLogo,
        skills: ['Figma', 'Adobe XD', 'Prototyping', 'Design Systems', 'Data Visualization'],
        link: 'https://nbo.om',
        summary: 'Spearheaded interactive prototypes and data-driven fintech mobile user experiences.',
        bulletPoints: [
          'Designed and developed <strong>20+ high-fidelity screens</strong> for a leading bank\'s mobile digital banking application.',
          'Proposed and prototyped 8+ data-driven UI/UX enhancements including AI chatbots and real-time portfolio dashboards.'
        ]
      }
    },
    research: {
      waterloo_compiler: {
        id: 'waterloo_compiler',
        title: 'Compiler Research Assistant',
        company: 'University of Waterloo',
        team: 'PLG (Programming Languages Group)',
        period: 'Dec 2025 – Apr 2026',
        roleType: 'Programming Languages & Systems',
        imageUrl: waterlooLogo,
        skills: ['Python', 'PPL Compilers (PAL)', 'ML Compilers (JAX)', 'SIMD / GPU'],
        link: 'https://plg.uwaterloo.ca/',
        summary: 'Compiler systems research under Prof. Yizhou Zhang developing semantic passes for probabilistic languages.',
        bulletPoints: [
          'Implementing a compiler pass for <strong>bit-blasting</strong> to discretize continuous distributions for Probabilistic Programming Languages (PPLs).',
          'Utilizing semantic lowering to <strong>JAX</strong> to target <strong>SIMD/GPU</strong> code for optimized inference with <strong>O(n)</strong> RV space-time complexity under <strong>Prof. Yizhou Zhang</strong>.'
        ]
      },
      waterloo_security: {
        id: 'waterloo_security',
        title: 'System Security Research Assistant',
        company: 'University of Waterloo',
        team: 'CrySP (Cryptography, Security & Privacy)',
        period: 'May 2025 – Aug 2025',
        roleType: 'TrustZone Security Research',
        imageUrl: waterlooLogo,
        skills: ['C++', 'C', 'OP-TEE', 'QEMU', 'Llama.cpp', 'Linux', 'Control Flow Attestation (CFA)'],
        link: 'https://crysp.uwaterloo.ca/',
        summary: 'Systems security research with Prof. Nadarajah Asokan enforcing hardware-backed CFI monitors for LLMs.',
        bulletPoints: [
          'Engineered a secure-world C++ CLI (<strong>llama.cpp</strong>) and <strong>OP-TEE TrustZone monitor</strong> to enforce control-flow integrity (CFI) of LLM agents.',
          'Validated interactions via cryptographic hashing to prevent prompt injection attacks under <strong>Prof. Nadarajah Asokan</strong>.'
        ]
      },
      stanford: {
        id: 'stanford',
        title: 'Data Analyst & Researcher',
        company: 'Stanford Policing Project',
        team: 'Open Policing Project',
        period: 'Jun 2024 – Jul 2024',
        roleType: 'Statistical Modeling & Analysis',
        imageUrl: stanfordLogo,
        skills: ['R', 'RStudio', 'Linear Regression', 'Statistical Modeling', 'Hypothesis Testing'],
        link: 'https://openpolicing.stanford.edu/',
        summary: 'Applied statistical modeling and residual diagnostics across municipal traffic enforcement datasets.',
        bulletPoints: [
          'Conducted statistical analysis on <strong>1,000+ traffic stops</strong> using R, Gaussian approximations, and linear regression modeling to evaluate bias trends.',
          'Built and visualized reports with ECDFs, QQ plots, and residual diagnostics with <5% margin of error.'
        ]
      }
    },
    extracurriculars: {
      startup: {
        id: 'startup',
        title: 'Co-Founder',
        company: 'Stealth Startup',
        period: 'Jan 2024 – Present',
        roleType: 'Founder',
        imageUrl: startupLogo,
        skills: ['Product', 'Strategy', 'Full Stack', 'Engineering'],
        summary: 'Collaborating across universities to build high-impact platforms for student developers.',
        bulletPoints: [
          'Building exciting new opportunities and platforms collaborating with universities and student developers.',
          'Driving technical vision and rapid prototype iterations.'
        ]
      },
      uwrt: {
        id: 'uwrt',
        title: 'Embedded Software Engineer Member',
        company: 'UWRobotics Team',
        period: 'Jun 2025 – Aug 2025',
        roleType: 'Robotics & Embedded Systems',
        imageUrl: uwrtLogo,
        skills: ['C', 'C++', 'IMU (MPU6050)', 'STM32', 'I2C', 'ROS'],
        link: 'https://uwrt.ca',
        summary: 'Low-level micro-controller firmware and sensor dynamics filtering for autonomous rover hardware.',
        bulletPoints: [
          'Developing <strong>orientation estimation software</strong> in C++ to process IMU sensor data of rover dynamics via <strong>I2C</strong> on STM32 microcontrollers.'
        ]
      },
      swift: {
        id: 'swift',
        title: 'Website Development Leader',
        company: 'SWIFT',
        period: 'Jun 2020 – Jan 2021',
        roleType: 'Non-Profit Lead',
        imageUrl: swiftLogo,
        skills: ['Web Development', 'Micro-lending', 'Leadership'],
        summary: 'Global non-profit micro-lending initiative supporting women entrepreneurs in emerging markets.',
        bulletPoints: [
          '<strong>Sustaining Women in Financial Turmoil</strong>: fundraising and micro-lending to women & small businesses in need.',
          'Helped raise more than <strong>$80,000</strong> and supported over <strong>2,700 women</strong> across <strong>70+ countries</strong>.'
        ]
      },
      mathletes: {
        id: 'mathletes',
        title: 'Co-Founder & Lead Coach',
        company: 'Mathletes',
        period: 'Aug 2021 – May 2023',
        roleType: 'Community & Education',
        imageUrl: mathletesLogo,
        skills: ['Competition Math', 'Pedagogy', 'Problem Solving'],
        summary: 'Student math club fostering problem-solving skills for olympiads and international contests.',
        bulletPoints: [
          'Fostered competitive mathematics and advanced problem-solving techniques among students.',
          'Trained students for international math tournaments (CEMC, AMC) and mentored junior cohorts.'
        ]
      },
      webgo: {
        id: 'webgo',
        title: 'Website Developer',
        company: 'WebGo',
        period: 'Jun 2024 – Jul 2024',
        roleType: 'Contract Developer',
        imageUrl: webgoLogo,
        skills: ['HTML5', 'CSS3', 'JavaScript', 'Responsive Design'],
        summary: 'Web consulting and engineering for community and charitable organizations.',
        bulletPoints: [
          'Developed and deployed modern, accessible websites for non-governmental organizations (NGOs).',
          'Collaborated directly with organization leaders to optimize UX and conversion.'
        ]
      }
    }
  };

  const handleCategoryClick = (category) => {
    if (category !== activeCategory) {
      setFadeOut(true);
      setTimeout(() => {
        setActiveCategory(category);
        const firstKey = Object.keys(experienceData[category])[0];
        setActiveExperience(firstKey);
        setFadeOut(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 150);
    }
  };

  const handleItemSelect = (key) => {
    setActiveExperience(key);
    const targetElement = cardRefs.current[key];
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="experience-page">
      <div className="experience-container">
        {/* Left Sidebar navigation */}
        <aside className="experience-sidebar">
          <div className="category-switcher">
            <button
              className={`cat-pill ${activeCategory === 'industry' ? 'active' : ''}`}
              onClick={() => handleCategoryClick('industry')}
            >
              Industry
            </button>
            <button
              className={`cat-pill ${activeCategory === 'research' ? 'active' : ''}`}
              onClick={() => handleCategoryClick('research')}
            >
              Research
            </button>
            <button
              className={`cat-pill ${activeCategory === 'extracurriculars' ? 'active' : ''}`}
              onClick={() => handleCategoryClick('extracurriculars')}
            >
              Activities
            </button>
          </div>

          <div className="experience-items-wrapper">
            <div className="experience-items-list" id="experience-items-list">
              {Object.entries(experienceData[activeCategory]).map(([key, item]) => (
                <button
                  key={key}
                  className={`item-button ${activeExperience === key ? 'active' : ''}`}
                  onClick={() => handleItemSelect(key)}
                >
                  <span className="item-button-company">{item.company}</span>
                  <span className="item-button-role">{item.title}</span>
                </button>
              ))}
            </div>
            <div className="scroll-hint-fade"></div>
          </div>

          {/* View Resume CTA in sidebar */}
          <div className="exp-resume-cta-wrap">
            <a
              href="https://drive.google.com/file/d/1Tcxl_JHiiWy25BhNDTKpnLurG3in8JWM/view?usp=drive_link"
              target="_blank"
              rel="noopener noreferrer"
              className="exp-action-cta-btn"
            >
              📄 View Full Resume
            </a>
          </div>
        </aside>

        {/* Right Content Area */}
        <main className="experience-content">
          <div className={`experience-cards-wrapper ${fadeOut ? 'fade-out' : ''}`}>
            {Object.entries(experienceData[activeCategory]).map(([key, item]) => (
              <div
                ref={(el) => (cardRefs.current[key] = el)}
                className={`exp-card ${activeExperience === key ? 'focused' : ''}`}
                key={key}
                onClick={() => setActiveExperience(key)}
              >
                <div className="exp-card-header">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.company} className="exp-logo-img" />
                  ) : (
                    <div className="exp-logo-placeholder">
                      {item.company.charAt(0)}
                    </div>
                  )}

                  <div className="exp-header-info">
                    <div className="exp-title-row">
                      <h3 className="exp-title">{item.title}</h3>
                      {item.badge && <span className="exp-badge">{item.badge}</span>}
                    </div>
                    <div className="exp-company-row">
                      <a
                        href={item.link || '#'}
                        target={item.link ? '_blank' : '_self'}
                        rel="noopener noreferrer"
                        className="exp-company-link"
                      >
                        {item.company}
                      </a>
                      {item.team && (
                        <>
                          <span className="exp-meta-separator">•</span>
                          <span className="exp-team-name">Team: {item.team}</span>
                        </>
                      )}
                      <span className="exp-meta-separator">•</span>
                      <span className="exp-period">{item.period}</span>
                    </div>
                  </div>
                </div>

                {item.summary && (
                  <p className="exp-summary">{item.summary}</p>
                )}

                {/* Skills tags */}
                {item.skills && item.skills.length > 0 && (
                  <div className="exp-skills-row">
                    {item.skills.map((skill, sIdx) => (
                      <span key={sIdx} className="exp-skill-tag">
                        {skill}
                      </span>
                    ))}
                  </div>
                )}

                {/* Bullets */}
                <ul className="exp-bullets">
                  {item.bulletPoints.map((bullet, bIdx) => (
                    <li
                      key={bIdx}
                      dangerouslySetInnerHTML={{ __html: bullet }}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Experience;

